import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    paymentMethod: '',
    isPaid: '',
    search: '',
    sortBy: 'createdAt',
    sortDir: 'desc'
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check for query params
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const statusParam = queryParams.get('status');
    
    if (statusParam) {
      setFilters(prev => ({ ...prev, status: statusParam }));
    }
  }, [location.search]);
  
  // Fetch orders
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }
        
        const res = await axios.get('/api/orders/all', {
          headers: {
            'x-auth-token': token
          }
        });
        
        setOrders(res.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load orders');
        setLoading(false);
      }
    };
    
    fetchOrders();
  }, []);
  
  // Apply filters and sorting
  useEffect(() => {
    let result = [...orders];
    
    // Filter by status
    if (filters.status) {
      result = result.filter(order => order.status === filters.status);
    }
    
    // Filter by payment method
    if (filters.paymentMethod) {
      result = result.filter(order => order.paymentMethod === filters.paymentMethod);
    }
    
    // Filter by payment status
    if (filters.isPaid === 'paid') {
      result = result.filter(order => order.isPaid);
    } else if (filters.isPaid === 'unpaid') {
      result = result.filter(order => !order.isPaid);
    }
    
    // Filter by search term (customer name or order ID)
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      result = result.filter(
        order => 
          order._id.toLowerCase().includes(searchTerm) || 
          order.user.name.toLowerCase().includes(searchTerm) ||
          (order.user.email && order.user.email.toLowerCase().includes(searchTerm))
      );
    }
    
    // Sort orders
    result.sort((a, b) => {
      let aValue, bValue;
      
      switch (filters.sortBy) {
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        case 'totalAmount':
          aValue = a.totalAmount;
          bValue = b.totalAmount;
          break;
        default:
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
      }
      
      return filters.sortDir === 'asc' ? aValue - bValue : bValue - aValue;
    });
    
    setFilteredOrders(result);
  }, [filters, orders]);
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  const clearFilters = () => {
    setFilters({
      status: '',
      paymentMethod: '',
      isPaid: '',
      search: '',
      sortBy: 'createdAt',
      sortDir: 'desc'
    });
    
    // Clear URL params
    navigate('/admin/orders');
  };
  
  const handleSort = (field) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortDir: prev.sortBy === field && prev.sortDir === 'desc' ? 'asc' : 'desc'
    }));
  };
  
  const handleViewOrder = (order) => {
    setSelectedOrder(order);
  };
  
  const closeOrderDetails = () => {
    setSelectedOrder(null);
  };
  
  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      setIsUpdating(true);
      
      const token = localStorage.getItem('token');
      
      const res = await axios.put(`/api/orders/${orderId}/status`, 
        { status: newStatus },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          }
        }
      );
      
      // Update orders list
      setOrders(prev => 
        prev.map(order => 
          order._id === orderId ? { ...order, status: newStatus } : order
        )
      );
      
      // Update selected order if open
      if (selectedOrder && selectedOrder._id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
      
      setIsUpdating(false);
    } catch (err) {
      console.error('Error updating order status:', err);
      alert('Failed to update order status');
      setIsUpdating(false);
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="neumorph p-8 rounded-full">
          <svg className="animate-spin h-8 w-8 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-8">
        <div className="neumorph p-6 rounded-lg inline-block">
          <h3 className="text-xl text-red-600 mb-2">Error</h3>
          <p className="text-gray-700">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="neumorph-btn mt-4 px-4 py-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">Manage Orders</h1>
      
      {/* Filters */}
      <div className="neumorph p-6 rounded-lg mb-8">
        <h2 className="text-xl font-semibold mb-4">Filters</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-gray-700 mb-2">Search</label>
            <input
              type="text"
              id="search"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              className="neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none"
              placeholder="Search by order ID or customer..."
            />
          </div>
          
          {/* Status Filter */}
          <div>
            <label htmlFor="status" className="block text-gray-700 mb-2">Statuss</label>
            <select
              id="status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          {/* Payment Method Filter */}
          <div>
            <label htmlFor="paymentMethod" className="block text-gray-700 mb-2">Payment Method</label>
            <select
              id="paymentMethod"
              name="paymentMethod"
              value={filters.paymentMethod}
              onChange={handleFilterChange}
              className="neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none"
            >
              <option value="">All Methods</option>
              <option value="payhere">PayHere</option>
              <option value="2checkout">2Checkout</option>
              <option value="cod">Cash on Delivery</option>
            </select>
          </div>
          
          {/* Payment Status Filter */}
          <div>
            <label htmlFor="isPaid" className="block text-gray-700 mb-2">Payment Status</label>
            <select
              id="isPaid"
              name="isPaid"
              value={filters.isPaid}
              onChange={handleFilterChange}
              className="neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none"
            >
              <option value="">All Payment Statuses</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </div>
        </div>
        
        <div className="mt-4 flex justify-end">
          <button
            onClick={clearFilters}
            className="neumorph-btn px-4 py-2"
          >
            Clear Filters
          </button>
        </div>
      </div>
      
      {/* Orders Table */}
      <div className="neumorph p-6 rounded-lg">
        <h2 className="text-xl font-semibold mb-4">Orders ({filteredOrders.length})</h2>
        
        {filteredOrders.length === 0 ? (
          <p className="text-gray-600 text-center py-4">No orders found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-semibold">
                    <button 
                      className="flex items-center"
                      onClick={() => handleSort('_id')}
                    >
                      Order ID
                      {filters.sortBy === '_id' && (
                        <span className="ml-1">
                          {filters.sortDir === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="pb-3 font-semibold">Customer</th>
                  <th className="pb-3 font-semibold">
                    <button 
                      className="flex items-center"
                      onClick={() => handleSort('createdAt')}
                    >
                      Date
                      {filters.sortBy === 'createdAt' && (
                        <span className="ml-1">
                          {filters.sortDir === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="pb-3 font-semibold">
                    <button 
                      className="flex items-center"
                      onClick={() => handleSort('totalAmount')}
                    >
                      Amount
                      {filters.sortBy === 'totalAmount' && (
                        <span className="ml-1">
                          {filters.sortDir === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="pb-3 font-semibold">Payment</th>
                  <th className="pb-3 font-semibold">Status</th>
                  <th className="pb-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(order => (
                  <tr key={order._id} className="border-b last:border-b-0">
                    <td className="py-4 text-sm">{order._id.substring(0, 8)}...</td>
                    <td className="py-4 text-sm">{order.user.name}</td>
                    <td className="py-4 text-sm">{new Date(order.createdAt).toLocaleDateString()}</td>
                    <td className="py-4 text-sm">${order.totalAmount.toFixed(2)}</td>
                    <td className="py-4 text-sm">
                      <div className="flex flex-col">
                        <span className="capitalize">{order.paymentMethod}</span>
                        <span className={order.isPaid ? 'text-green-600 text-xs' : 'text-red-600 text-xs'}>
                          {order.isPaid ? 'Paid' : 'Unpaid'}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(order.status)}`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                    </td>
                    <td className="py-4 text-sm">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewOrder(order)}
                          className="neumorph-btn px-3 py-1 text-sm"
                        >
                          View
                        </button>
                        <select
                          value={order.status}
                          onChange={(e) => handleUpdateStatus(order._id, e.target.value)}
                          className="neumorph-inset px-2 py-1 text-sm rounded-lg focus:outline-none"
                          disabled={isUpdating}
                        >
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="neumorph bg-neumorph rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Order Details</h2>
              <button 
                onClick={closeOrderDetails}
                className="neumorph p-2 rounded-full"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Order Info */}
              <div className="neumorph-inset p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Order Information</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order ID:</span>
                    <span>{selectedOrder._id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span>{new Date(selectedOrder.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(selectedOrder.status)}`}>
                      {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Amount:</span>
                    <span className="font-semibold">${selectedOrder.totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              {/* Customer Info */}
              <div className="neumorph-inset p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Customer Information</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name:</span>
                    <span>{selectedOrder.user.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span>{selectedOrder.user.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Phone:</span>
                    <span>{selectedOrder.user.phone || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Shipping Info */}
              <div className="neumorph-inset p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Shipping Address</h3>
                <div className="space-y-1">
                  <p>{selectedOrder.shippingAddress.street}</p>
                  <p>{selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.state} {selectedOrder.shippingAddress.postalCode}</p>
                  <p>{selectedOrder.shippingAddress.country}</p>
                </div>
              </div>
              
              {/* Payment Info */}
              <div className="neumorph-inset p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Payment Information</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Method:</span>
                    <span className="capitalize">{selectedOrder.paymentMethod}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Status:</span>
                    <span className={selectedOrder.isPaid ? 'text-green-600' : 'text-red-600'}>
                      {selectedOrder.isPaid ? 'Paid' : 'Unpaid'}
                    </span>
                  </div>
                  {selectedOrder.isPaid && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment Date:</span>
                        <span>{new Date(selectedOrder.paidAt).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment ID:</span>
                        <span>{selectedOrder.paymentId || 'N/A'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Order Items */}
            <div className="neumorph-inset p-4 rounded-lg mb-6">
              <h3 className="font-semibold mb-3">Order Items</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="pb-2 font-semibold">Product</th>
                      <th className="pb-2 font-semibold">Price</th>
                      <th className="pb-2 font-semibold">Quantity</th>
                      <th className="pb-2 font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.products.map((item, index) => (
                      <tr key={index} className="border-b last:border-b-0">
                        <td className="py-3">
                          <div className="flex items-center">
                            {item.product.imageUrl && (
                              <div className="neumorph p-1 rounded-lg mr-3">
                                <img 
                                  src={item.product.imageUrl} 
                                  alt={item.product.name} 
                                  className="w-10 h-10 object-cover rounded-lg"
                                />
                              </div>
                            )}
                            <span>{item.product.name}</span>
                          </div>
                        </td>
                        <td className="py-3">${item.price.toFixed(2)}</td>
                        <td className="py-3">{item.quantity}</td>
                        <td className="py-3">${(item.price * item.quantity).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t">
                      <td colSpan="3" className="py-3 text-right font-semibold">Total:</td>
                      <td className="py-3 font-bold">${selectedOrder.totalAmount.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            
            {/* Update Status */}
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <span className="font-medium">Update Status:</span>
                <select
                  value={selectedOrder.status}
                  onChange={(e) => handleUpdateStatus(selectedOrder._id, e.target.value)}
                  className="neumorph-inset px-3 py-2 rounded-lg focus:outline-none"
                  disabled={isUpdating}
                >
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <button 
                onClick={closeOrderDetails}
                className="neumorph-btn px-4 py-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
